// QC / AQL inspections and Compliance & Certification: the rules of FALCON PLM v2.0.41, shared by the server
// (which enforces them and works out results) and the screens (which show the live result while typing).
import type { Workflow } from "./orders.js";

// ---- Sampling plans (ISO 2859-1 / ANSI-ASQ Z1.4, normal single sampling) ------------------------------------
export const QC_LEVELS = ["I", "II", "III", "S-1", "S-2", "S-3", "S-4", "100%"] as const;
export const QC_AQL_VALUES = ["0.65", "1", "1.5", "2.5", "4", "6.5"] as const;
export const AQL_CHOICES = { critical: ["0", "0.65", "1", "1.5"], major: ["0.65", "1", "1.5", "2.5", "4"], minor: ["1", "1.5", "2.5", "4", "6.5"] } as const;
export const SAMPLING_STANDARDS = ["Customer Standard", "ISO 2859-1 / ANSI-ASQ Z1.4", "100% Inspection", "Other"] as const;
export type SamplingStandard = (typeof SAMPLING_STANDARDS)[number];
export const INSPECTION_TYPES = ["Inline", "Pre-final", "Final Random", "Re-inspection", "100% Inspection"] as const;

const SAMPLE_SIZES: Record<string, number> = { A: 2, B: 3, C: 5, D: 8, E: 13, F: 20, G: 32, H: 50, J: 80, K: 125, L: 200, M: 315, N: 500, P: 800, Q: 1250, R: 2000 };
const CODE_ROWS: Array<{ min: number; max: number } & Record<string, string | number>> = [
  { min: 2, max: 8, I: "A", II: "A", III: "B", "S-1": "A", "S-2": "A", "S-3": "A", "S-4": "A" },
  { min: 9, max: 15, I: "A", II: "B", III: "C", "S-1": "A", "S-2": "A", "S-3": "A", "S-4": "A" },
  { min: 16, max: 25, I: "B", II: "C", III: "D", "S-1": "A", "S-2": "A", "S-3": "B", "S-4": "B" },
  { min: 26, max: 50, I: "C", II: "D", III: "E", "S-1": "A", "S-2": "B", "S-3": "B", "S-4": "C" },
  { min: 51, max: 90, I: "C", II: "E", III: "F", "S-1": "B", "S-2": "B", "S-3": "C", "S-4": "C" },
  { min: 91, max: 150, I: "D", II: "F", III: "G", "S-1": "B", "S-2": "B", "S-3": "C", "S-4": "D" },
  { min: 151, max: 280, I: "E", II: "G", III: "H", "S-1": "B", "S-2": "C", "S-3": "D", "S-4": "E" },
  { min: 281, max: 500, I: "F", II: "H", III: "J", "S-1": "B", "S-2": "C", "S-3": "D", "S-4": "E" },
  { min: 501, max: 1200, I: "G", II: "J", III: "K", "S-1": "C", "S-2": "C", "S-3": "E", "S-4": "F" },
  { min: 1201, max: 3200, I: "H", II: "K", III: "L", "S-1": "C", "S-2": "D", "S-3": "E", "S-4": "G" },
  { min: 3201, max: 10000, I: "J", II: "L", III: "M", "S-1": "C", "S-2": "D", "S-3": "F", "S-4": "G" },
  { min: 10001, max: 35000, I: "K", II: "M", III: "N", "S-1": "C", "S-2": "D", "S-3": "F", "S-4": "H" },
  { min: 35001, max: 150000, I: "L", II: "N", III: "P", "S-1": "D", "S-2": "E", "S-3": "G", "S-4": "J" },
  { min: 150001, max: 500000, I: "M", II: "P", III: "Q", "S-1": "D", "S-2": "E", "S-3": "G", "S-4": "J" },
  { min: 500001, max: Infinity, I: "N", II: "Q", III: "R", "S-1": "D", "S-2": "E", "S-3": "H", "S-4": "K" },
];
type Pair = [number, number];
const AC_RE: Record<string, Record<string, Pair>> = {
  A: { "0.65": [0, 1], "1": [0, 1], "1.5": [0, 1], "2.5": [0, 1], "4": [0, 1], "6.5": [0, 1] },
  B: { "0.65": [0, 1], "1": [0, 1], "1.5": [0, 1], "2.5": [0, 1], "4": [0, 1], "6.5": [0, 1] },
  C: { "0.65": [0, 1], "1": [0, 1], "1.5": [0, 1], "2.5": [0, 1], "4": [0, 1], "6.5": [0, 1] },
  D: { "0.65": [0, 1], "1": [0, 1], "1.5": [0, 1], "2.5": [0, 1], "4": [0, 1], "6.5": [1, 2] },
  E: { "0.65": [0, 1], "1": [0, 1], "1.5": [0, 1], "2.5": [0, 1], "4": [1, 2], "6.5": [2, 3] },
  F: { "0.65": [0, 1], "1": [0, 1], "1.5": [0, 1], "2.5": [1, 2], "4": [2, 3], "6.5": [3, 4] },
  G: { "0.65": [0, 1], "1": [0, 1], "1.5": [1, 2], "2.5": [2, 3], "4": [3, 4], "6.5": [5, 6] },
  H: { "0.65": [0, 1], "1": [1, 2], "1.5": [2, 3], "2.5": [3, 4], "4": [5, 6], "6.5": [7, 8] },
  J: { "0.65": [1, 2], "1": [2, 3], "1.5": [3, 4], "2.5": [5, 6], "4": [7, 8], "6.5": [10, 11] },
  K: { "0.65": [2, 3], "1": [3, 4], "1.5": [5, 6], "2.5": [7, 8], "4": [10, 11], "6.5": [14, 15] },
  L: { "0.65": [3, 4], "1": [5, 6], "1.5": [7, 8], "2.5": [10, 11], "4": [14, 15], "6.5": [21, 22] },
  M: { "0.65": [5, 6], "1": [7, 8], "1.5": [10, 11], "2.5": [14, 15], "4": [21, 22], "6.5": [21, 22] },
  N: { "0.65": [7, 8], "1": [10, 11], "1.5": [14, 15], "2.5": [21, 22], "4": [21, 22], "6.5": [21, 22] },
  P: { "0.65": [10, 11], "1": [14, 15], "1.5": [21, 22], "2.5": [21, 22], "4": [21, 22], "6.5": [21, 22] },
  Q: { "0.65": [14, 15], "1": [21, 22], "1.5": [21, 22], "2.5": [21, 22], "4": [21, 22], "6.5": [21, 22] },
  R: { "0.65": [21, 22], "1": [21, 22], "1.5": [21, 22], "2.5": [21, 22], "4": [21, 22], "6.5": [21, 22] },
};

export function codeLetter(lotSize: number, level: string): string {
  if (level === "100%") return "100%";
  const lot = Math.max(1, Number(lotSize) || 0);
  const row = CODE_ROWS.find((r) => lot >= r.min && lot <= r.max) || CODE_ROWS[0];
  return String(row[level] ?? row.II);
}
export interface ClassPlan { codeLetter: string; sampleSize: number; accept: number; reject: number }
export function planFor(lotSize: number, level: string, aql: string): ClassPlan {
  const lot = Math.max(1, Number(lotSize) || 0);
  if (level === "100%") return { codeLetter: "100%", sampleSize: lot, accept: 0, reject: 1 };
  const code = codeLetter(lot, level);
  const sampleSize = Math.min(SAMPLE_SIZES[code] || lot, lot);
  if (String(aql) === "0") return { codeLetter: code, sampleSize, accept: 0, reject: 1 };
  const pair = AC_RE[code]?.[String(parseFloat(aql))] || [0, 1];
  return { codeLetter: code, sampleSize, accept: pair[0], reject: pair[1] };
}

// ---- The organisation's internal inspection standard (the v2.0.41 "RIMAN standard", now a setting) --------
export interface InternalStandard { sampleQty: number; critical: Pair; major: Pair; minor: Pair }
export const DEFAULT_INTERNAL_STANDARD: InternalStandard = { sampleQty: 8, critical: [0, 1], major: [1, 2], minor: [2, 3] };
export function normalizeInternalStandard(x: any): InternalStandard {
  const pair = (v: any, d: Pair): Pair => {
    const ac = Math.max(0, Math.floor(Number(v?.[0]))), re = Math.max(0, Math.floor(Number(v?.[1])));
    return Number.isFinite(ac) && Number.isFinite(re) && re > ac ? [ac, re] : d;
  };
  const q = Math.floor(Number(x?.sampleQty));
  return {
    sampleQty: Number.isFinite(q) && q >= 1 && q <= 10000 ? q : DEFAULT_INTERNAL_STANDARD.sampleQty,
    critical: pair(x?.critical, DEFAULT_INTERNAL_STANDARD.critical), major: pair(x?.major, DEFAULT_INTERNAL_STANDARD.major), minor: pair(x?.minor, DEFAULT_INTERNAL_STANDARD.minor),
  };
}

/** The lot-size ranges of ISO 2859-1 Table 1 with the code letter of an inspection level (for the reference table). */
export function lotRanges(level: string): Array<{ min: number; max: number; letter: string }> {
  return CODE_ROWS.map((r) => ({ min: r.min, max: r.max, letter: String(r[level] ?? r.II) }));
}

// ---- The QC standard: the internal standard above plus the AQL plan the step-by-step inspection uses ----------------
// One place (Quality → QC → Inspection Standard). The four original fields (random sample and Ac / Re per class) keep
// their saved values; the AQL fields are the options the earlier report form offered (ISO 2859-1 / ANSI-ASQ Z1.4,
// normal inspection, single sampling).
export const STANDARD_LEVELS = QC_LEVELS;
export interface QcStandard extends InternalStandard { aqlCritical: string; aqlMajor: string; aqlMinor: string; level: string }
export const DEFAULT_STANDARD_AQL = { aqlCritical: "0", aqlMajor: "2.5", aqlMinor: "4", level: "II" } as const;
export function normalizeQcStandard(x: any): QcStandard {
  const pick = (v: unknown, list: readonly string[], d: string) => {
    const t = String(v ?? "");
    const hit = list.find((o) => o === t || (t !== "" && Number(o) === Number(t)));
    return hit ?? d;
  };
  return {
    ...normalizeInternalStandard(x),
    aqlCritical: pick(x?.aqlCritical, AQL_CHOICES.critical, DEFAULT_STANDARD_AQL.aqlCritical),
    aqlMajor: pick(x?.aqlMajor, AQL_CHOICES.major, DEFAULT_STANDARD_AQL.aqlMajor),
    aqlMinor: pick(x?.aqlMinor, AQL_CHOICES.minor, DEFAULT_STANDARD_AQL.aqlMinor),
    level: pick(x?.level, STANDARD_LEVELS, DEFAULT_STANDARD_AQL.level),
  };
}

// ---- The inspection report --------------------------------------------------------------------------------
export const CHECKPOINTS: Array<[string, string]> = [
  ["workmanship", "Workmanship"], ["measurements", "Measurements"], ["fabric", "Fabric / Material"],
  ["colour", "Colour / Shade"], ["stitching", "Stitching / Seams"], ["branding", "Branding / Artwork"],
  ["labels", "Labels / Wash Care"], ["reflective", "Reflective Tape / Trims"], ["packing", "Packing / Polybags"],
  ["carton", "Carton Marking / Barcode"], ["quantity", "Quantity / Assortment"],
  // Stage 2.6E: bulk is compared with the gold seal sample (its photos are shown with the report).
  ["goldSeal", "Bulk matches gold seal sample"],
];
/** The checkpoints with the organisation's own name for the gold seal. */
export const checkpointsNamed = (sealName: string): Array<[string, string]> =>
  CHECKPOINTS.map(([k, l]) => [k, k === "goldSeal" ? `Bulk matches ${sealName.toLowerCase()} sample` : l]);
export const CHECK_RESULTS = ["Pass", "Fail", "N/A"] as const;
export const SEVERITIES = ["Critical", "Major", "Minor"] as const;
export type Severity = (typeof SEVERITIES)[number];
/**
 * The language-free codes saved next to the English words (Pass → pass, N/A → na, Critical → critical), so an answer means
 * the same whatever language the inspector's screen was in. The English word stays saved too and is what reports show.
 */
export const RESULT_CODE: Record<string, "pass" | "fail" | "na"> = { Pass: "pass", Fail: "fail", "N/A": "na" };
export const SEVERITY_CODE: Record<string, "critical" | "major" | "minor"> = { Critical: "critical", Major: "major", Minor: "minor" };
export interface Defect { severity: Severity; severityCode?: string; qty: number; description: string; location: string; correctiveAction: string }
export interface MeasureCheck { pom: string; size: string; spec: string; tolerance: string; actual: string; result: "" | "Pass" | "Fail" | "N/A"; resultCode?: string }
export interface QcReport {
  inspectionDate: string; inspector: string; inspectionType: string; factory: string; orderQty: number; inspectedQty: number | null;
  samplingStandard: SamplingStandard; inspectionLevel: string; overrideInternal: boolean; sampleCode: string; requiredSampleQty: number | null;
  aqlCritical: string; aqlMajor: string; aqlMinor: string;
  acCritical: number | null; reCritical: number | null; acMajor: number | null; reMajor: number | null; acMinor: number | null; reMinor: number | null;
  checkpoints: Record<string, { result: string; resultCode?: string; notes: string }>;
  defects: Defect[]; measurements: MeasureCheck[];
  generalNotes: string; overallNotes: string; correctiveAction: string;
}
const num = (v: unknown): number | null => {
  if (v === null || v === undefined || String(v).trim() === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const s = (v: unknown, max = 5000) => String(v ?? "").trim().slice(0, max);

const withSeverity = (severity: Severity) => ({ severity, severityCode: SEVERITY_CODE[severity] });
const withResult = (result: MeasureCheck["result"]) => ({ result, resultCode: RESULT_CODE[result] || "" });

export function normalizeReport(x: any, orderQty = 0): QcReport {
  const standard = (SAMPLING_STANDARDS as readonly string[]).includes(x?.samplingStandard) ? x.samplingStandard : "Customer Standard";
  const level = (QC_LEVELS as readonly string[]).includes(x?.inspectionLevel) ? x.inspectionLevel : "II";
  const pick = (v: any, list: readonly string[], d: string) => (list.includes(String(v)) ? String(v) : d);
  const checkpoints: QcReport["checkpoints"] = {};
  for (const [k] of CHECKPOINTS) {
    const c = x?.checkpoints?.[k] || {};
    const result = (CHECK_RESULTS as readonly string[]).includes(c.result) ? c.result : "";
    checkpoints[k] = { result, resultCode: RESULT_CODE[result] || "", notes: s(c.notes, 500) };
  }
  return {
    inspectionDate: /^\d{4}-\d{2}-\d{2}$/.test(String(x?.inspectionDate || "")) ? x.inspectionDate : "",
    inspector: s(x?.inspector, 120), inspectionType: pick(x?.inspectionType, INSPECTION_TYPES, "Final Random"), factory: s(x?.factory, 200),
    orderQty: Math.max(0, Number(orderQty) || Number(x?.orderQty) || 0), inspectedQty: num(x?.inspectedQty),
    samplingStandard: standard, inspectionLevel: level, overrideInternal: !!x?.overrideInternal, sampleCode: s(x?.sampleCode, 10), requiredSampleQty: num(x?.requiredSampleQty),
    aqlCritical: pick(x?.aqlCritical, AQL_CHOICES.critical, "0"), aqlMajor: pick(x?.aqlMajor, AQL_CHOICES.major, "2.5"), aqlMinor: pick(x?.aqlMinor, AQL_CHOICES.minor, "4"),
    acCritical: num(x?.acCritical), reCritical: num(x?.reCritical), acMajor: num(x?.acMajor), reMajor: num(x?.reMajor), acMinor: num(x?.acMinor), reMinor: num(x?.reMinor),
    checkpoints,
    defects: (Array.isArray(x?.defects) ? x.defects : []).slice(0, 300).map((d: any) => ({
      ...withSeverity((SEVERITIES as readonly string[]).includes(d?.severity) ? d.severity : "Minor"), qty: Math.max(0, Math.floor(Number(d?.qty) || 0)),
      description: s(d?.description, 500), location: s(d?.location, 200), correctiveAction: s(d?.correctiveAction, 500),
    })).filter((d: Defect) => d.qty || d.description || d.location || d.correctiveAction),
    measurements: (Array.isArray(x?.measurements) ? x.measurements : []).slice(0, 1000).map((m: any) => ({
      pom: s(m?.pom, 200), size: s(m?.size, 30), spec: s(m?.spec, 40), tolerance: s(m?.tolerance, 40), actual: s(m?.actual, 40),
      ...withResult(["Pass", "Fail", "N/A"].includes(m?.result) ? m.result : ""),
    })).filter((m: MeasureCheck) => m.pom || m.size || m.spec || m.actual),
    generalNotes: s(x?.generalNotes), overallNotes: s(x?.overallNotes), correctiveAction: s(x?.correctiveAction),
  };
}

/** Is the plan calculated automatically (ISO/ANSI or 100%)? */
export const autoStandard = (std: string) => std === "ISO 2859-1 / ANSI-ASQ Z1.4" || std === "100% Inspection";

/**
 * Fill in the sampling plan the way v2.0.41 did: ISO/ANSI and 100% work out the sample code, required quantity
 * and Ac/Re; Customer Standard uses the organisation's internal standard unless "override" is ticked; Other is manual.
 */
export function applyPlan(r: QcReport, internal: InternalStandard): QcReport {
  const out = { ...r };
  const lot = r.orderQty;
  if (autoStandard(r.samplingStandard)) {
    const level = r.samplingStandard === "100% Inspection" ? "100%" : r.inspectionLevel === "100%" ? "II" : r.inspectionLevel;
    if (r.samplingStandard === "100% Inspection") out.inspectionLevel = "100%";
    const c = planFor(lot, level, r.aqlCritical), m = planFor(lot, level, r.aqlMajor), n = planFor(lot, level, r.aqlMinor);
    out.sampleCode = codeLetter(lot, level);
    out.requiredSampleQty = Math.max(c.sampleSize, m.sampleSize, n.sampleSize);
    [out.acCritical, out.reCritical, out.acMajor, out.reMajor, out.acMinor, out.reMinor] = [c.accept, c.reject, m.accept, m.reject, n.accept, n.reject];
  } else {
    out.sampleCode = "CUSTOM";
    if (r.samplingStandard === "Customer Standard" && !r.overrideInternal) {
      [out.acCritical, out.reCritical] = internal.critical; [out.acMajor, out.reMajor] = internal.major; [out.acMinor, out.reMinor] = internal.minor;
    }
  }
  return out;
}

/** A new report: the organisation's internal standard, a small random sample, today's date (as v2.0.41). */
export function blankReport(opts: { orderQty: number; round: number; inspector: string; factory: string; today: string; internal: InternalStandard }): QcReport {
  const sample = Math.max(1, Math.min(opts.internal.sampleQty, opts.orderQty || opts.internal.sampleQty));
  return applyPlan(normalizeReport({
    inspectionDate: opts.today, inspector: opts.inspector, inspectionType: opts.round >= 2 ? "Re-inspection" : "Final Random", factory: opts.factory,
    inspectedQty: sample, samplingStandard: "Customer Standard", inspectionLevel: "S-2", requiredSampleQty: sample, aqlCritical: "0", aqlMajor: "2.5", aqlMinor: "4",
  }, opts.orderQty), opts.internal);
}

export function defectTotals(r: Pick<QcReport, "defects">): Record<Severity, number> {
  const t = { Critical: 0, Major: 0, Minor: 0 };
  for (const d of r.defects || []) t[d.severity] += Math.max(0, Number(d.qty) || 0);
  return t;
}

/** What still needs filling in before the report can be submitted (field key and label). */
export function missingFields(r: QcReport): Array<{ key: string; label: string }> {
  const out: Array<{ key: string; label: string }> = [];
  if (!r.inspectionDate) out.push({ key: "inspectionDate", label: "Inspection Date" });
  if (!r.inspector) out.push({ key: "inspector", label: "Inspector" });
  const actual = r.inspectedQty || 0, required = r.requiredSampleQty || 0;
  if (actual <= 0) out.push({ key: "inspectedQty", label: "Actual Inspected Qty" });
  else if (required > 0 && actual < required) out.push({ key: "inspectedQty", label: `Actual Inspected Qty (minimum ${required.toLocaleString("en-GB")})` });
  for (const [k, l] of [["acCritical", "Critical Accept (Ac)"], ["reCritical", "Critical Reject (Re)"], ["acMajor", "Major Accept (Ac)"], ["reMajor", "Major Reject (Re)"], ["acMinor", "Minor Accept (Ac)"], ["reMinor", "Minor Reject (Re)"]] as const) {
    if (r[k] === null) out.push({ key: k, label: l });
  }
  for (const [k, l] of CHECKPOINTS) if (!r.checkpoints[k]?.result) out.push({ key: `checkpoint_${k}`, label: l });
  return out;
}

export type QcResult = "Incomplete" | "Pass" | "Fail";
/** v2.0.41: fail if any class exceeds its Accept number, any checkpoint failed or any measurement failed. */
export function calculateResult(r: QcReport): QcResult {
  if (missingFields(r).length) return "Incomplete";
  const t = defectTotals(r);
  if (t.Critical > (r.acCritical ?? 0) || t.Major > (r.acMajor ?? 0) || t.Minor > (r.acMinor ?? 0)) return "Fail";
  if (CHECKPOINTS.some(([k]) => r.checkpoints[k]?.result === "Fail")) return "Fail";
  if (r.measurements.some((m) => m.result === "Fail")) return "Fail";
  return "Pass";
}

/** "PO-CODE-QC1" as in v2.0.41. */
export const reportNumber = (poNumber: string, code: string, round: number) => `${poNumber || "PO"}-${code || "ITEM"}-QC${round}`;

/** Final QC state of a line from its two reviews: 1st PASS, or 1st FAIL then 2nd PASS; both FAIL is a final fail. */
export function finalQc(first: string, second: string): "Pending" | "Pass" | "Fail" {
  if (first === "Pass") return "Pass";
  if (first === "Fail") return second === "Pass" ? "Pass" : second === "Fail" ? "Fail" : "Pending";
  return "Pending";
}

/**
 * Put the review results on the Time & Action Plan. A FAIL sends Bulk Production back to In Progress and takes
 * the line off Ready to Ship (v2.0.41). Rounds without an inspection keep what the Time & Action Plan already had.
 */
export function workflowFromReviews(w: Workflow, reviews: Array<{ round: number; status: string; date: string | null; by: string }>): Workflow {
  const n: Workflow = structuredClone(w);
  // QC = the first round; "qc2" = the latest re-inspection (QC2, QC3…), so a later pass counts.
  const latest = reviews.filter((r) => r.round >= 2).sort((a, b) => b.round - a.round)[0];
  for (const r of [...reviews.filter((x) => x.round === 1), ...(latest ? [latest] : [])]) {
    const key = r.round >= 2 ? "qc2" : "qc";
    const status = r.status === "Pass" || r.status === "Fail" ? r.status : "Pending";
    n[key] = status === "Pending" ? { status } : { status, date: r.date || undefined, by: r.by };
    if (status === "Fail") {
      if (n.bulk && n.bulk.status !== "In Progress") n.bulk = { status: "In Progress" };
      if (n.readyToShip?.value) n.readyToShip = { value: false };
      delete n.qcSkipped;   // a real QC result replaces an Admin's skip
    }
  }
  if (n.qc?.status === "Pass") delete n.qc2;
  return n;
}

// ---- Compliance & Certification ---------------------------------------------------------------------------------
/** Certificate types (the register); older types from v2.0.41 records stay valid. */
export const CERTIFICATE_TYPES = ["EU type-examination certificate", "UKCA certificate", "Test report", "Oeko-Tex", "Factory audit", "Other"] as const;
export const LEGACY_CERTIFICATE_TYPES = ["Fabric Certificate", "Test Report", "Declaration of Conformity", "Supplier Certificate", "Product Certificate"] as const;
export const COMPLIANCE_TYPES = CERTIFICATE_TYPES;
export const COMPLIANCE_RECORD_TYPES = [...CERTIFICATE_TYPES, ...LEGACY_CERTIFICATE_TYPES] as const;
/** What a certificate can be linked to (many of each). */
export const CERT_TARGETS = [["garment", "Garments"], ["fabric", "Fabrics"], ["supplier", "Suppliers"]] as const;
export type CertTarget = (typeof CERT_TARGETS)[number][0];
/** Kept for records from v2.0.41 (one link each). */
export const COMPLIANCE_SCOPES = ["Fabric", "Supplier"] as const;
export const COMPLIANCE_ALL_SCOPES = ["Fabric", "Supplier", "Product", "Tech Pack", "General"] as const;
export const EXPIRY_WARNING_DAYS = 90;
export type ComplianceStatus = "Valid" | "Expiring" | "Expired" | "Withdrawn";
export const COMPLIANCE_STATUSES: ComplianceStatus[] = ["Valid", "Expiring", "Expired", "Withdrawn"];

/** Reminder days before expiry (0 = on the expiry date) and the PO warning window. */
export interface CertificateSettings { reminderDays: number[]; poWarnDays: number }
export const DEFAULT_CERTIFICATE_SETTINGS: CertificateSettings = { reminderDays: [90, 60, 30, 0], poWarnDays: 30 };
export function normalizeCertificateSettings(v: any): CertificateSettings {
  const days = Array.isArray(v?.reminderDays) ? [...new Set(v.reminderDays.map((x: unknown) => Math.floor(Number(x))).filter((x: number) => Number.isFinite(x) && x >= 0 && x <= 365))] as number[] : DEFAULT_CERTIFICATE_SETTINGS.reminderDays;
  const warn = Math.floor(Number(v?.poWarnDays));
  return { reminderDays: days.sort((a, b) => b - a).slice(0, 8), poWarnDays: Number.isFinite(warn) && warn >= 0 && warn <= 180 ? warn : 30 };
}
/** The reminder step due today: the smallest "days before" not above the days left (null when none is due). */
export function reminderStep(daysLeft: number | null, steps: number[]): number | null {
  if (daysLeft === null || daysLeft < 0) return null;
  const due = steps.filter((s) => daysLeft <= s);
  return due.length ? Math.min(...due) : null;
}

export function daysUntil(date: string | null | undefined, today: string): number | null {
  if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{4}-\d{2}-\d{2}$/.test(today)) return null;
  return Math.round((Date.parse(`${date}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000);
}
/** Valid (no expiry, or more than 90 days left), Expiring (90 days or less), Expired, or Withdrawn by hand. */
export function complianceStatus(expiryDate: string | null | undefined, today: string, withdrawn = false): ComplianceStatus {
  if (withdrawn) return "Withdrawn";
  const d = daysUntil(expiryDate, today);
  if (d === null) return "Valid";
  if (d < 0) return "Expired";
  if (d <= EXPIRY_WARNING_DAYS) return "Expiring";
  return "Valid";
}
export const STATUS_RANK: Record<ComplianceStatus, number> = { Expired: 0, Expiring: 1, Valid: 2, Withdrawn: 3 };
export const validStatus = (s: ComplianceStatus) => s === "Valid" || s === "Expiring";

/** Certificate problems that stop (expired) or warn about (expiring soon) issuing a PO. */
export interface CertIssue { level: "block" | "warn"; lineId: string; line: string; certificateId: string; text: string }
export interface CertForCheck { id: string; type: string; standards: string[]; number: string; expiryDate: string | null; withdrawn: boolean; required: boolean; targets: Array<{ type: CertTarget; id: string }> }
/**
 * The PO rule: for each line, the required certificates of its garment and main fabric. An expired one blocks
 * (unless a valid certificate of the same type and standard for the same garment / fabric replaced it); one expiring
 * within `warnDays` warns. Withdrawn certificates don't count.
 */
export function poCertificateIssues(lines: Array<{ id: string; label: string; garmentId: string | null; fabricId: string | null; fabricLabel?: string }>, certs: CertForCheck[], today: string, warnDays: number): CertIssue[] {
  const out: CertIssue[] = [];
  const norm = (x: string) => x.trim().toLowerCase();
  const covers = (a: CertForCheck, b: CertForCheck) => a.type === b.type && (a.standards.length === 0 && b.standards.length === 0 || a.standards.some((s) => b.standards.map(norm).includes(norm(s))));
  for (const l of lines) {
    for (const [kind, id, what] of [["garment", l.garmentId, "garment"], ["fabric", l.fabricId, `main fabric ${l.fabricLabel || ""}`.trim()]] as const) {
      if (!id) continue;
      const mine = certs.filter((c) => !c.withdrawn && c.targets.some((t) => t.type === kind && t.id === id));
      for (const c of mine.filter((x) => x.required)) {
        const d = daysUntil(c.expiryDate, today);
        if (d === null) continue;
        const name = `${c.type}${c.standards.length ? ` (${c.standards.join(", ")})` : ""}${c.number ? ` ${c.number}` : ""}`;
        const later = mine.filter((x) => x.id !== c.id && covers(x, c) && (daysUntil(x.expiryDate, today) ?? 99999) > d);
        const best = Math.max(d, ...later.map((x) => daysUntil(x.expiryDate, today) ?? 99999));
        if (best < 0) out.push({ level: "block", lineId: l.id, line: l.label, certificateId: c.id, text: `${l.label}: the ${what}'s ${name} expired on ${c.expiryDate}.` });
        else if (d >= 0 && d <= warnDays && best <= warnDays) out.push({ level: "warn", lineId: l.id, line: l.label, certificateId: c.id, text: `${l.label}: the ${what}'s ${name} expires on ${c.expiryDate} (${d} day${d === 1 ? "" : "s"}).` });
      }
    }
  }
  return out;
}

/**
 * A garment's compliance at a glance (list badge): each standard it claims should be backed by current
 * evidence for its main fabric (or, for records kept from v2.0.41, the garment or its Tech Pack).
 */
export function garmentCompliance(standards: string[], records: Array<{ standard: string; status: ComplianceStatus }>) {
  const norm = (x: string) => x.trim().toLowerCase();
  const wanted = [...new Set(standards.map(norm).filter(Boolean))];
  const missing = wanted.filter((st) => !records.some((r) => norm(r.standard) === st && validStatus(r.status)));
  const expired = records.filter((r) => r.status === "Expired").length;
  const expiring = records.filter((r) => r.status === "Expiring").length;
  const tone = expired ? "expired" : missing.length ? "missing" : expiring ? "expiring" : records.length ? "covered" : "none";
  const label = expired ? `${expired} expired` : missing.length ? `${missing.length} missing` : expiring ? `${expiring} expiring` : records.length ? "Covered" : "No records";
  return { tone, label, required: wanted.length, missing, expired, expiring, records: records.length };
}
